/**
 * GuestEntryView — Tier-0 entry: nickname only, no email/password required.
 * Calls POST /identity/enter to provision a guest account, stores the JWT,
 * and routes the user to /home.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';
import { validateNickname } from '../validators';
import { enterGuest } from '../AuthService';

const TOKEN_KEY = 'learnature_token';

export async function mountGuestEntryView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_guest_title'));
  layout.setSubtitle(t('auth_guest_subtitle'));

  let loading = false;

  const form = document.createElement('form');
  form.setAttribute('novalidate', '');
  form.autocomplete = 'on';

  // Nickname field
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';

  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = t('auth_guest_nickname_label');
  labelEl.setAttribute('for', 'auth-nickname');

  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = 'text';
  input.id = 'auth-nickname';
  input.name = 'nickname';
  input.autocomplete = 'username';
  input.maxLength = 48;
  input.placeholder = t('auth_guest_nickname_placeholder');
  input.setAttribute('aria-label', t('auth_guest_nickname_label'));

  wrapper.append(labelEl, input);
  form.appendChild(wrapper);

  // Disclaimer
  const disclaimer = document.createElement('p');
  disclaimer.style.cssText = 'font-size:0.78rem;color:var(--text-secondary,#94A3B8);margin:-4px 0 16px;line-height:1.4;';
  disclaimer.textContent = t('auth_guest_disclaimer');
  form.appendChild(disclaimer);

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'auth-btn auth-btn-primary';
  submitBtn.innerHTML = `<span>${t('auth_guest_button')}</span>`;
  form.appendChild(submitBtn);

  // Back to entry
  const backRow = document.createElement('div');
  backRow.className = 'auth-footer-row';
  const backLink = document.createElement('button');
  backLink.type = 'button';
  backLink.className = 'auth-link';
  backLink.textContent = t('auth_guest_back');
  backLink.addEventListener('click', () => opts.onNavigate('entry'));
  backRow.appendChild(backLink);
  form.appendChild(backRow);

  // Submit handler
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;

    layout.clearMessages();
    const nickname = input.value.trim();

    const v = validateNickname(nickname);
    if (!v.valid) {
      layout.showError(t(v.errors[0]));
      input.focus();
      return;
    }

    loading = true;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<span class="auth-spinner"></span>`;

    const result = await enterGuest(nickname, ctx.i18n.locale);

    if (result.ok) {
      // Store the JWT identical to the login path so the rest of the app picks it up.
      localStorage.setItem(TOKEN_KEY, result.data.token);
      document.cookie = `${TOKEN_KEY}=${result.data.token};path=/;max-age=86400;SameSite=Lax`;

      const returnPath = sessionStorage.getItem('aweni_auth_return') || '/home';
      sessionStorage.removeItem('aweni_auth_return');
      ctx.navigate(returnPath);

      window.dispatchEvent(new CustomEvent('aweni:auth-changed'));
    } else {
      const err = result.error;
      let msg = t(`auth_error_${err.code?.toLowerCase() || 'unknown'}`);
      if (msg.startsWith('auth_error_')) msg = err.error;
      layout.showError(msg);
    }

    loading = false;
    submitBtn.disabled = false;
    submitBtn.innerHTML = `<span>${t('auth_guest_button')}</span>`;
  });

  layout.card.appendChild(form);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  // Auto-focus the nickname field
  setTimeout(() => input.focus(), 200);

  return {
    unmount() {
      container.innerHTML = '';
    },
  };
}
